import type { User } from '../types/auth';
import { loadSignup } from '../features/signup/signupStorage';
import { loadTravelBallFamilySignup } from '../features/travel-ball/signup/tbFamilySignupStorage';
import { loadTravelBallProgramSignup } from '../features/travel-ball/signup/tbProgramSignupStorage';
import { returnPathFrom } from './returnPath';

/** The signup pages that can resume a signup stored in this browser. */
export type PendingSignupPath =
  | '/signup'
  | '/travel-ball/signup/program'
  | '/travel-ball/signup/family';

/**
 * Which signup page holds a resumable signup for this address in this browser: the college signup,
 * then travel ball program, then travel ball family (player or
 * parent/guardian). Null when none does, or when there is no address.
 *
 * More than one is stored only when someone started a second signup without leaving the first
 * ("Use a different email" and sign-out clear them all). The family signup comes last because only
 * that order can still finish both on one address: the API adds the parent/guardian role to an
 * existing coach account (`role_added`), but refuses a coach signup from a player or parent/guardian
 * account (`family_role_conflict`). So finishing the coach signup first loses nothing.
 *
 * The loaders discard a stored signup that belongs to another address (and one read with no address
 * at all), so they are never called without one: a tab with no session would otherwise wipe the
 * coach's signup before the tab that holds the session could resume it.
 */
export const pendingSignupPath = (email: string | null | undefined): PendingSignupPath | null => {
  if (!email) return null;
  if (loadSignup(email)) return '/signup';
  if (loadTravelBallProgramSignup(email)) return '/travel-ball/signup/program';
  if (loadTravelBallFamilySignup(email)) return '/travel-ball/signup/family';
  return null;
};

/**
 * A signed-in Firebase account with no RosterPilot user behind it. Only a confirmed 401 from
 * `/api/auth/me` counts; a profile-less user from a transient failure is left alone.
 *
 * That is either an unfinished signup (college self-serve or travel ball), or someone whose
 * RosterPilot user was removed (staff taken off a program, a deleted account). Which one is told
 * apart by `isUnlinkedAccount`.
 */
export const isProfileMissing = (user: User | null | undefined): boolean =>
  user != null && !user.profile && user.profileMissing === true;

/**
 * A RosterPilot user with nowhere to work: `/api/auth/me` answered 200 with `views: []`, so there is
 * no college seat, travel ball program membership or family role behind the account. A travel ball
 * coach removed from their program, or one whose team had no program (those teams were removed on
 * 2026-10-30).
 *
 * Only a server-confirmed empty list counts: `views` is absent on an older API and on the fallback
 * user built after a transient failure, and neither says the account has no workspace.
 */
export const hasNoWorkspace = (user: User | null | undefined): boolean =>
  user != null && !isProfileMissing(user) && Array.isArray(user.views) && user.views.length === 0;

/**
 * An account that cannot use the app and has no signup under way in this browser:
 *
 * - profile-less (a confirmed 401 from `/api/auth/me`) with no stored signup form for its address,
 *   college or travel ball. Most often removed staff, who must not be dropped silently into a signup.
 * - a user with no workspace at all (`hasNoWorkspace`).
 *
 * The routes send such a user to `/login`, which signs them out and says the email is not linked to
 * an active account. `/login` keeps them there rather than sending them on to `/`, which would send
 * them straight back (the landing router has no home for an account with no views). A profile-less
 * account with a matching stored signup is sent to that signup's page to resume it.
 */
export const isUnlinkedAccount = (user: User | null | undefined): boolean =>
  (isProfileMissing(user) && pendingSignupPath(user?.email) === null) || hasNoWorkspace(user);

/**
 * Where a signed-in, profile-less user belongs: the signup page holding the signup stored for their
 * address, otherwise `/login` (which signs them out with an explanation).
 */
export const profileMissingTarget = (user: User): PendingSignupPath | '/login' =>
  pendingSignupPath(user.email) ?? '/login';

/**
 * Where `/login` sends someone who is already signed in and not an unlinked account (that one stays
 * on `/login` to be signed out): an unfinished signup back to its signup page (never to a return
 * path, which would only bounce them there anyway), anyone else to the path a guard stashed, or the
 * landing router at `/` which picks the right home per active view (Phase 1b.5).
 */
export const signedInLoginTarget = (user: User, locationState: unknown): string =>
  isProfileMissing(user)
    ? (pendingSignupPath(user.email) ?? '/signup')
    : (returnPathFrom(locationState) ?? '/');
